// Ported from LC2 (src/features/coreApi/metricsSlice.js) — `getAccountESG` (EsgTab/
// EsgComponent) plus `getEngagementByAccountId` (EngagementTab, see
// pages/Dashboard/Engagement/EngagementSection.js), added the same way. `getAccountESG` has
// since been repointed at a different, confirmed-live endpoint (see the comment on it
// below) that's account-scoped only — LC2's separate community-level ESG endpoint
// (`getCommunityESG`, esg_dashboard/property/{sfid}/) has been removed since there's no
// confirmed per-community equivalent of the new one; EsgSection.js now always shows the
// account-wide report regardless of which community is selected.
// Extended for the Performance tab port (pages/Dashboard/Performance/): `getUnitsWithConcessions`
// (AccountPerformance/CommunityPerformance's UnitsWithConcessions), `getDaysOnMarketByCommunitySfid`
// (CommunityPerformance's DaysOnMarket), `getConcessionSavingByCampaignSfid` /
// `getConcessionSavingByPropertySfid` + their update mutations (CampaignPerformance's
// ConcessionComps), and `updateFeatureVisibility` (the admin-only VisibilitySwitch shared by
// DaysOnMarket/ConcessionComps) — all copied verbatim from LC2, same URL/params shape.
// Extended for the "At a glance" port (pages/Dashboard/Glance/): `getGlanceData` (LC2's
// Glance.js — `accounts/{sfid}/at-a-glance-data/`, returns `{data, tabs}`) and
// `getVacancyComps` (LC2's VacancyGlance.js's per-row comparison panel — `accounts/{account}/
// property/{property}/vacancy/{id}/comps/`, same params/transformResponse/transformErrorResponse/
// keepUnusedDataFor shape), both copied verbatim from LC2.
// LC2's metricsSlice.js also defines other unrelated endpoints (getDaysOnMarketByCampaignSfid,
// delinquency, renewals, stake pay, leasing velocity, claimed-rate, alerts-like, etc.) for
// tabs/components that haven't been ported to the new UI yet (Delinquency is commented out in
// every LC2 Performance/*.js caller too — see Performance/AccountPerformance.js and
// CommunityPerformance.js header comments) — those are intentionally left out until the pages
// that need them are ported, at which point they should be added back here following the same
// injectEndpoints pattern (see communitiesSlice.js / residentsSlice.js).
import {coreApiSlice} from './apiSlice';

// GET esg/?account_id=... is confirmed to return the same rows-based contract as current
// LC's esg_dashboard (not yet deployed everywhere, so the previous flat shape is still
// handled below):
// {success, status_code, data: {first_block, second_block, third_block, is_esg_enabled}, msg}
// Each block is {title, description, rows: [{title, value, size, display_tooltip,
// tooltip_text, description, description_1, description_2, icon, icon_element}, ...]}.
// The axios baseQuery already unwraps to `data`, and EsgComponent.js / FinancialUnlocks.js /
// StakerInsights.js / EsgMetrics.js consume {first_block, second_block, third_block} as-is.
// The flat mapper + copy below is only a fallback for the pre-deploy
// {total_residents, financial_unlocks, staker_insights, esg_metrics} body.
const STAKER_INSIGHT_LABELS = {
    top_bankers_percentile: {title: 'Top Bankers', description: 'Credit-building activity vs. the Stake Network'},
    top_unlockers_percentile: {title: 'Top Unlockers', description: 'Financial unlocks activity vs. the Stake Network'},
    top_connectors_percentile: {title: 'Top Connectors', description: 'Stake Checking adoption vs. the Stake Network'}
};

// Order matches EsgMetrics.js's fixed METRIC_ICONS sequence (cash back, credit score,
// savings, credit builder, on-time payments, visa debit, express paycheck, active checking)
// so each metric keeps its established icon.
const ESG_METRIC_LABELS = [
    ['cash_back_earned', 'Cash Back Earned'],
    ['avg_credit_score_increase', 'Avg. Credit Score Increase'],
    ['avg_savings_increase', 'Avg. Savings Increase'],
    ['credit_builder_enrollments', 'Credit Builder Enrollments'],
    ['on_time_payments_reported', 'On-Time Payments Reported'],
    ['visa_debit_activations', 'Stake Visa Debit Activations'],
    ['express_paychecks_delivered', 'Express Paychecks Delivered'],
    ['active_checking_accounts', 'Active Checking Accounts']
];

// Matches stake-core-api's own create_value_for_staker_insight formatting ("Above {value}%
// of Stake Portfolios") so this reads the same as the rows-based esg_dashboard endpoint did.
const formatPercentileValue = (raw) => {
    const num = Math.round(parseFloat(raw));
    return Number.isFinite(num) ? `Above ${num}% of Stake Portfolios` : raw;
};

const mapEsgReportResponse = (response) => {
    if (!response) {
        return {first_block: null, second_block: null, third_block: null};
    }

    // Rows-based /api/esg contract (matches current LC). Pass the blocks through so
    // titles, descriptions, tooltips, and preformatted values stay backend-owned.
    if (response.first_block || response.second_block || response.third_block) {
        return {
            first_block: response.first_block || null,
            second_block: response.second_block || null,
            third_block: response.third_block || null,
            is_esg_enabled: response.is_esg_enabled
        };
    }

    const {total_residents, financial_unlocks, staker_insights, esg_metrics, is_esg_enabled} = response;

    const firstBlock = financial_unlocks && {
        title: 'Financial Unlocks',
        description: total_residents
            ? `Track the financial impact Stake has had on your ${total_residents} residents.`
            : 'Track the financial impact Stake has had on your residents.',
        rows: [
            {title: 'Total Unlocks', value: financial_unlocks.total_unlocks},
            {title: 'Unlocks per Resident', value: financial_unlocks.unlocks_per_resident}
        ]
    };

    const secondBlock = staker_insights && {
        title: 'Staker Insights',
        description: 'Detailed metrics of how Stake is financially empowering your residents',
        rows: Object.entries(staker_insights)
            .filter(([key]) => STAKER_INSIGHT_LABELS[key])
            .map(([key, value]) => ({
                title: STAKER_INSIGHT_LABELS[key].title,
                description: STAKER_INSIGHT_LABELS[key].description,
                value: formatPercentileValue(value)
            }))
    };

    const thirdBlock = esg_metrics && {
        title: 'ESG Metrics',
        description: 'The collective ESG impact of your Stake program',
        rows: ESG_METRIC_LABELS.filter(([key]) => esg_metrics[key] !== undefined).map(([key, title]) => ({
            title,
            value: esg_metrics[key]
        }))
    };

    return {first_block: firstBlock, second_block: secondBlock, third_block: thirdBlock, is_esg_enabled};
};

export const coreMetricsApi = coreApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // NOTE: unlike esg_dashboard/account/{sfid}/'s `communities` param (permission-
        // scoping restricted users to only their permitted communities), this endpoint's
        // confirmed contract only takes `account_id` — no communities-filtering param is
        // confirmed to exist yet, so restricted-permission users currently see the full
        // account's report here. Flagging as a gap to revisit once/if the backend adds one.
        getAccountESG: builder.query({
            query: ({sfid}) => ({
                url: 'esg/',
                method: 'get',
                params: {account_id: sfid}
            }),
            transformResponse: mapEsgReportResponse,
            providesTags: (_result, _error, category) => [{type: 'ESG', id: category}]
        }),
        getEngagementByAccountId: builder.query({
            query: ({sfid, communities}) => ({
                url: `lc_homepage/account/${sfid}/`,
                method: 'get',
                params: {communities}
            }),
            providesTags: (_result, _error, category) => [{type: 'Engagement', id: category}]
        }),
        getDaysOnMarketByCommunitySfid: builder.query({
            query: (sfid) => ({
                url: `rental_communities/${sfid}/days_on_market_with_comps/`,
                method: 'get'
            }),
            providesTags: [{type: 'Days on market', id: 'LIST'}]
        }),
        updateFeatureVisibility: builder.mutation({
            query: (params) => {
                const sfid = params.sfid;
                delete params.sfid;
                return {
                    method: 'patch',
                    url: `lc_features/change_visibility/${sfid}/`,
                    params: {...params}
                };
            }
        }),
        getConcessionSavingByCampaignSfid: builder.query({
            query: (sfid) => ({
                url: `campaign/${sfid}/concessions_savings/`,
                method: 'get'
            }),
            providesTags: [{type: 'Concession savings', id: 'LIST'}]
        }),
        updateConcessionSavingByCampaignSfid: builder.mutation({
            query: ({sfid, params}) => ({
                url: `campaign/${sfid}/concessions_savings/`,
                method: 'get',
                params: {...params}
            })
        }),
        getConcessionSavingByPropertySfid: builder.query({
            query: (sfid) => ({
                url: `property/${sfid}/concessions_savings/`,
                method: 'get'
            }),
            providesTags: [{type: 'Concession savings', id: 'LIST'}]
        }),
        updateConcessionSavingByPropertySfid: builder.mutation({
            query: ({sfid, params}) => ({
                url: `property/${sfid}/concessions_savings/`,
                method: 'get',
                params: {...params}
            })
        }),
        getUnitsWithConcessions: builder.query({
            query: (sfid) => ({
                method: 'get',
                url: `account/${sfid}/units_with_concessions`
            })
        }),
        getGlanceData: builder.query({
            query: (sfid) => ({
                url: `accounts/${sfid}/at-a-glance-data/`,
                method: 'get'
            }),
            providesTags: [{type: 'Glance', id: 'LIST'}]
        }),
        getVacancyComps: builder.query({
            query: ({account_sfid, property_sfid, id, params}) => ({
                url: `accounts/${account_sfid}/property/${property_sfid}/vacancy/${id}/comps/`,
                method: 'get',
                params: {...params}
            }),
            providesTags: (result, error, {id}) => [{type: 'Comps', id}, 'Comps'],
            transformResponse: (response) => response,
            transformErrorResponse: (response) => ({status: response.status, message: response.error}),
            keepUnusedDataFor: 300
        })
    }),
    overrideExisting: false
});

export const {
    useLazyGetAccountESGQuery,
    useLazyGetEngagementByAccountIdQuery,
    useGetDaysOnMarketByCommunitySfidQuery,
    useUpdateFeatureVisibilityMutation,
    useGetConcessionSavingByCampaignSfidQuery,
    useGetConcessionSavingByPropertySfidQuery,
    useUpdateConcessionSavingByCampaignSfidMutation,
    useUpdateConcessionSavingByPropertySfidMutation,
    useGetUnitsWithConcessionsQuery,
    useGetGlanceDataQuery,
    useGetVacancyCompsQuery
} = coreMetricsApi;
